import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { component, ComponentPolicy, property } from '../../../concepts/objects';
import { memoUsing } from '../../../concepts/objects/MemoUsing';
import { AdapterInfo, CreateAdapterInfo } from '../../canvas/AdapterInfo';
import { EditorSession } from '../../canvas/EditorSession';
import { Physical, PotentialInteraction } from '../../canvas/entities/Physical';
import { RectangularPhysical } from '../../canvas/entities/RectangularPhysical';
import { Resizable } from '../../canvas/entities/Resizable';
import { Snappable } from '../../canvas/entities/Snappable';
import { TextAlignment } from '../../canvas/entities/TextConventions';
import { UIField } from '../../canvas/entities/UIField';
import { TextboxLayout } from './TextboxLayout';
import { textboxPropertyLayout } from './TextboxPropertyLayouts';
import { TextboxValueField } from './TextboxValueField';

const invocation = defineCommand(
    'textboxInvoke',
    'Textbox: Invoke',
    (r, [id]: [string]) => {
        r.call(EditorSession).update(AdapterInfo, CreateAdapterInfo(id, 'field', 'value', { hideProps: true }));
    },
    [],
);

const mutation = defineCommand(
    'textboxMutate',
    'Textbox: Mutate',
    (r, [id]: [string]) => {
        r.call(EditorSession).update(AdapterInfo, CreateAdapterInfo(id, 'field'));
    },
    [],
);

const textboxDef = {
    name: 'Textbox',
    implements: [Physical, Resizable, Snappable],
    invocation,
    mutation,
    propertyLayout: textboxPropertyLayout,
};

const textboxPolicy = ComponentPolicy.noChildren('Textbox');

@component(textboxDef)
export class Textbox extends RectangularPhysical implements Physical, Snappable {

    @property({ type: 'string' })
    public background!: string;

    @property({ type: 'string', oldName: 'value|value' })
    public value!: string;

    @property({ type: 'string', default: 'Inter', oldName: 'value|fontFamily', style: true })
    public fontFamily!: string;

    @property({ type: 'string', oldName: 'value|fontColor', style: true })
    public fontColor!: string;

    @property({ type: 'number', default: 24, oldName: 'value|fontSize', style: true })
    public fontSize!: number;

    @property({ type: 'number', default: TextAlignment.start, oldName: 'value|textAlignH', style: true })
    public textAlignH!: TextAlignment;

    @property({ type: 'number', default: TextAlignment.start, oldName: 'value|textAlignH', style: true })
    public textAlignV!: TextAlignment;

    public get boundary() {
        return true;
    }

    public get shouldSnap() {
        return true;
    }

    @memoUsing('width', 'height', 'background', 'fontFamily', 'fontColor', 'fontSize', 'textAlignH', 'textAlignV', 'value')
    public get layout() {
        return TextboxLayout(this);
    }

    @memoUsing('layout')
    public get uiFields(): readonly UIField[] {
        return [TextboxValueField(this)];
    }

    protected get policy() {
        return textboxPolicy;
    }

    public testInteraction({ type, mpt, selected }: PotentialInteraction): [boolean, string] {
        if (this.localBounds.contains(this.toRelative(mpt))) {
            if (type !== 'invoke' || !selected) {
                return [true, 'default'];
            }
        }
        return [false, 'none'];
    }
}
